// 生きたお見合い — ボス共通「攻撃後の一手」の抽選。
// 設計: docs/candidates/2026-07-19-boss-living-standoff-design.md
//
// 骨子: 攻撃 (recovery) が終わった瞬間に press / breathe / reset を重み付き抽選する。
//   ★ランダムにするのは「長さ」ではなく「どれが来るか」。 各手の長さは固定で学習できる。
//     根拠: ホロウナイトのマンティスロード (固定待ち) は公平と評され、 ラディアンス
//     (待ち時間ランダム+技ランダム+位置ランダム) は「RNGがひどい」と批判されている。
//     待ち時間のランダム化は、そのまま体感の理不尽さに直結する。
//   ★天井つきガチャ: maxInARow (連続出禁) と maxSkipped (強制発動) で
//     「同じ手が3連続」「あの手が永遠に来ない」を機構で禁止する。 運が悪い日を作らない。
//     参考: ホロウナイトの SendRandomEventV3 (weights + eventMax + missedMax)。
//
// 既定は press 100% = 現行挙動と完全一致。 ボスごとに重みを振った分だけ変わる。
import type { Boss, BossStandoffConfig, StandoffBeat } from './types'

export const STANDOFF_BEATS: readonly StandoffBeat[] = ['press', 'breathe', 'reset'] as const

// 未指定時の既定値。 weights 未指定 = press だけが出る = 現行挙動。
export const STANDOFF_DEFAULTS = {
  // 緊張帯 (260〜400ms) の中央付近に置く。 「1発入るが戻り切れない」= 手を出すか迷う長さ。
  //   安全側に倒したいボスは 400ms 以上、 罠寄りにしたいボスは 260ms 未満へ振る。
  breatheMs: 360,
  resetMs: 420,
  resetSpeedPx: 200,
} as const

// 決定的だが単調でないハッシュ (boss.ts の idleGapMsFor と同じ流儀)。
//   Math.random を使わないのは、 bot 計測 (combat_sim) の固定seed再現性を壊さないため。
export function hash01(n: number): number {
  const s = Math.sin(n * 127.1) * 43758.5453
  return s - Math.floor(s)
}

export function standoffWeights(cfg?: BossStandoffConfig): Record<StandoffBeat, number> {
  const w = cfg?.weights
  if (!w) return { press: 1, breathe: 0, reset: 0 }
  return { press: w.press ?? 0, breathe: w.breathe ?? 0, reset: w.reset ?? 0 }
}

// この一手が idle を占有する長さ (ms)。 press は 0 = 何も挟まない = 現行挙動。
export function standoffBeatMs(beat: StandoffBeat, cfg?: BossStandoffConfig): number {
  if (beat === 'breathe') return cfg?.breatheMs ?? STANDOFF_DEFAULTS.breatheMs
  if (beat === 'reset') return cfg?.resetMs ?? STANDOFF_DEFAULTS.resetMs
  return 0
}

export type StandoffPick = {
  beat: StandoffBeat
  inARow: number
  skips: Partial<Record<StandoffBeat, number>>
}

// 次の一手を選ぶ。 seed は決定的にするための種 (attackCount を渡す)。
export function pickStandoffBeat(
  cfg: BossStandoffConfig | undefined,
  seed: number,
  prev: StandoffBeat,
  prevInARow: number,
  prevSkips: Partial<Record<StandoffBeat, number>> | undefined,
): StandoffPick {
  const weights = standoffWeights(cfg)
  const skips = { press: 0, breathe: 0, reset: 0, ...(prevSkips ?? {}) }
  const live = STANDOFF_BEATS.filter((b) => weights[b] > 0)
  if (live.length === 0) return { beat: 'press', inARow: 1, skips: {} }

  // ① 強制発動 (maxSkipped) が最優先 = 「あの手が永遠に来ない」を機構で禁止する。
  //    複数が同時に天井へ達したら、 より長く待たされている方を優先する。
  let forced: StandoffBeat | null = null
  let forcedSkips = -1
  for (const b of live) {
    const cap = cfg?.maxSkipped?.[b]
    if (cap != null && (skips[b] ?? 0) >= cap && (skips[b] ?? 0) > forcedSkips) {
      forced = b
      forcedSkips = skips[b] ?? 0
    }
  }

  // ② 通常は重み付き抽選。 ただし maxInARow に達している手は候補から外す。
  let chosen: StandoffBeat
  if (forced) {
    chosen = forced
  } else {
    const cap = cfg?.maxInARow?.[prev]
    const pool = live.filter((b) => !(b === prev && cap != null && prevInARow >= cap))
    // 全部弾かれたら press へ逃がす (連続上限で手詰まりにしない)。
    const usable = pool.length > 0 ? pool : live
    const total = usable.reduce((sum, b) => sum + weights[b], 0)
    let roll = hash01(seed * 3.7 + 1.9) * total
    chosen = usable[usable.length - 1]!
    for (const b of usable) {
      roll -= weights[b]
      if (roll <= 0) { chosen = b; break }
    }
  }

  // ③ カウンタ更新: 選ばれた手は飛ばし回数リセット、 出番のある他の手は +1。
  const nextSkips: Partial<Record<StandoffBeat, number>> = {}
  for (const b of live) nextSkips[b] = b === chosen ? 0 : (skips[b] ?? 0) + 1
  return {
    beat: chosen,
    inARow: chosen === prev ? prevInARow + 1 : 1,
    skips: nextSkips,
  }
}

// ---- boss.ts の idle 処理から使うヘルパ ----

export function currentStandoffBeat(boss: Boss): StandoffBeat {
  return boss.standoffBeat ?? 'press'
}

// この tick はまだ「一手」の最中か。 press は常に false = 現行挙動を一切変えない。
export function inStandoffBeat(boss: Boss, cfg: BossStandoffConfig | undefined, elapsedMs: number): boolean {
  const beat = currentStandoffBeat(boss)
  if (beat === 'press') return false
  return elapsedMs < standoffBeatMs(beat, cfg)
}

// 仕切り直し中の x 速度 (px/s)。 プレイヤーから離れる向き。 それ以外の手では 0。
export function standoffResetVelocity(
  boss: Boss,
  cfg: BossStandoffConfig | undefined,
  playerX: number,
  elapsedMs: number,
): number {
  if (currentStandoffBeat(boss) !== 'reset') return 0
  if (!inStandoffBeat(boss, cfg, elapsedMs)) return 0
  const speed = cfg?.resetSpeedPx ?? STANDOFF_DEFAULTS.resetSpeedPx
  return (boss.x >= playerX ? 1 : -1) * speed
}
